import { useCallback, useEffect, useState } from 'react';
import { isFeatureOff } from '../../services/dealersApi';
import { dynamicConfigApi } from '../../services/dynamicConfigApi';
import { DynamicConfigEntry } from '../../types/dynamicConfig';

export const DEALER_CREDIT_SWITCH_KEY = 'garagat.dealer-credit.enabled';
/**
 * The dealer wallet switch (Maher 2026-11-03), default off: credit packs, the prepaid dealer balance and
 * assigning from it. The master switch above still gates the rest; the wallet needs both on.
 */
export const DEALER_WALLET_SWITCH_KEY = 'garagat.dealer-credit.wallet.enabled';

/** `unknown` = this admin cannot read the switches (not a Runtime Config admin): keep the UI as it is. */
export type WalletState = 'on' | 'off' | 'unknown';

const isOn = (e: DynamicConfigEntry) => (e.value ?? 'false').trim() === 'true';

/**
 * The wallet state from the Runtime Config list: on only when the wallet switch AND the master switch are
 * on. No override = the deployed default, which is off for both. A key missing from the list (filtered
 * per screen) says nothing, except that a visible master switch that is off turns the wallet off too.
 */
export function walletStateOf(entries: DynamicConfigEntry[] | null | undefined): WalletState {
  const all = entries ?? [];
  const master = all.find((e) => e.key === DEALER_CREDIT_SWITCH_KEY);
  const wallet = all.find((e) => e.key === DEALER_WALLET_SWITCH_KEY);
  if (master && !isOn(master)) return 'off';
  if (!wallet) return 'unknown';
  if (!isOn(wallet)) return 'off';
  return master ? 'on' : 'unknown';
}

/**
 * Spec 085 D14 / BR-33 — with `garagat.dealer-credit.enabled` off, every admin dealer write answers
 * 404 `FEATURE_OFF` (reads stay open, so lists still load). A page tracks that here and shows the
 * banner instead of an error.
 *
 * The admin dealer API has no gated read to ask up front, so with a token the hook also peeks at the
 * switch in the Runtime Config list once. Only admins holding `dynamic-config` see that key (the list
 * is filtered per screen); for everyone else the peek finds nothing, any failure is ignored, and the
 * banner comes on at the first refused write.
 *
 * The same single read also gives the wallet switch (`wallet`, see `walletStateOf`): `walletOff` is true only
 * when known off, so pack and dealer-balance surfaces stay as they are for admins who cannot read it; a
 * refused pack write then reports it through `markWalletOff`.
 */
export function useFeatureOff(token?: string | null) {
  const [off, setOff] = useState(false);
  const [wallet, setWallet] = useState<WalletState>('unknown');

  useEffect(() => {
    if (!token) return;
    let live = true;
    dynamicConfigApi.list(token)
      .then((all) => {
        if (!live) return;
        const entry = (all ?? []).find((e) => e.key === DEALER_CREDIT_SWITCH_KEY);
        // No override = the deployed default, which is off (DealerCreditSettings.enabled).
        if (entry && !isOn(entry)) setOff(true);
        setWallet(walletStateOf(all));
      })
      .catch(() => {});
    return () => { live = false; };
  }, [token]);

  /** True (and the banner on) when `err` is FEATURE_OFF; the caller then skips its own error. */
  const catchOff = useCallback((err: unknown) => {
    if (!isFeatureOff(err)) return false;
    setOff(true);
    return true;
  }, []);
  /** For a dialog that already recognised FEATURE_OFF and closed itself. */
  const markOff = useCallback(() => setOff(true), []);
  /** A wallet write (pack create/edit) answered FEATURE_OFF: the wallet is off, whichever switch did it. */
  const markWalletOff = useCallback(() => setWallet('off'), []);
  const walletOff = off || wallet === 'off';
  return { off, catchOff, markOff, wallet, walletOff, markWalletOff };
}

/** Shown where wallet surfaces (credit packs, the prepaid dealer balance) would be while the wallet is off. */
export function WalletOffNotice({ off }: { off: boolean }) {
  if (!off) return null;
  return (
    <div className="admin-info-banner warning dealer-feature-off" role="status" data-testid="dealer-wallet-off">
      <strong>The dealer wallet is switched off.</strong>{' '}
      Dealers cannot buy credit packs, hold a prepaid balance or assign credit from it; packs can be read but not
      created or changed. Per-buyer credit requests and mark-paid work as usual. The wallet is changed in Runtime
      Config only on Maher&apos;s written go.
    </div>
  );
}

export default function FeatureOffBanner({ off }: { off: boolean }) {
  if (!off) return null;
  return (
    <div className="admin-info-banner warning dealer-feature-off" role="status" data-testid="dealer-feature-off">
      <strong>Dealer credit is switched off.</strong>{' '}
      You can still read dealers, mark received payments and mark dealer refunds paid; other changes are
      refused until the switch is back on. It is changed in Runtime Config only on Maher&apos;s written go.
    </div>
  );
}
